import {observer} from 'mobx-react-lite'
import React, {useEffect, useState} from 'react'
import {Hub} from 'yougile-model'
import {Items} from '@api/model/items'
import {useBoardStore} from '@components/board/board-store'
import {ShowArchivedButton} from '@components/columns/show-archived-button'
import {ShowMoreButton} from '@components/columns/show-more-button'
import {Loader} from '@components/common/base/loader'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {Droppable} from '@components/droppable'
import {TaskCard} from '@components/task-card'
import {TaskCardDrag} from '@components/task-card/task-card-drag'
import {UiElementFor} from '@components/ui-element-for'
import {useForceUpdate, useSyncEvent} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {
  QueuedRender,
  taskCardPlaceholderClassName
} from '@containers/queued-render'
import {getScrollYToCenterDelta} from '@containers/scroll-utils'
import {TimeoutRender} from '@containers/timeout-render'
import {globals} from '@extensions/globals-base'
import {useColumnStore} from '../column-store'
import {TwSubtaskPlaceholder} from '../containers'

export const BasicColumn = observer(() => {
  const [state, store] = useColumnStore()
  const [, boardStore] = useBoardStore()
  const group = store.column

  const forceUpdate = useForceUpdate()
  useSyncEvent(
    'custom',
    {filter: group?.id, companyId: group?.companyId, debounceTime: 50},
    forceUpdate,
    [group]
  )

  return (
    <WithPerfectScrollbar
      ref={x => x && store.setScrollBar(x)}
      containerRef={store.setScrollableEl}
      onMouseDown={boardStore.boardDragScroll()}
      wheelPropagation
      wheelSpeed={1.5}
      className="px-8 -mx-8 column-scroll-wrapper"
      onScroll={boardStore.state.tasksRenderQueue.requestToUpdatePriorities}
    >
      <div className="h-full task-group-content" data-column-id={group.id}>
        <UiElementFor objId={group.id}>
          <div className="ui-cnt empty:hidden" />
        </UiElementFor>
        {!state.loading && <BasicColumnContent />}
      </div>
    </WithPerfectScrollbar>
  )
})

export const BasicColumnContent = observer(() => {
  const [state, store] = useColumnStore()
  const [boardState, boardStore] = useBoardStore()
  const group = store.column

  const taskCountLimit = store.getTaskCountLimit(group)
  const tasksOpen = store.getOpenTasks(group)
  const tasksOpenSorted = store.getOpenSortedTasks(group)
  const [showLoader, setShowLoader] = useState(!!tasksOpenSorted.length)
  const hideLoader = () => setShowLoader(false)

  const tasksArchived = store.getClosedTasks(group)
  const tasksLeft = tasksOpen.length - taskCountLimit
  const tasksArchivedLeft = tasksArchived.length - taskCountLimit

  // Small padding is required for task outline not to be cropped due to overflow hidden
  const antiCropPadTop = !!tasksOpenSorted.length

  useScrollToFirstArchived()

  return (
    <div
      className={cn(
        'proj-task-container',
        'space-y-8',
        'pb-8',
        antiCropPadTop && 'pt-2'
      )}
    >
      <TwSubtaskPlaceholder className="hidden" />

      <Droppable
        accepts={['task']}
        container=".task-group-container"
        showPlaceholder
        onDrop={drag => {
          boardStore.onTaskDrop({
            drag,
            toColumn: group,
            columnStore: store
          })
        }}
        className="empty:hidden"
      />

      {tasksOpenSorted.map((task, i) => (
        <Task key={task.id} idx={i} task={task} />
      ))}

      {showLoader && (
        <TimeoutRender timeout={1000}>
          <div className="text-center">
            <Loader size="xsmall" />
          </div>
        </TimeoutRender>
      )}

      <QueuedRender
        queue={boardState.tasksRenderQueue}
        id={group?.location}
        onBeforeRender={hideLoader}
      >
        <>
          {tasksLeft > 0 && (
            <ShowMoreButton
              count={tasksLeft}
              onClick={() => store.taskCountShowMore(group)}
              className="mt-8"
              noBorder={globals.theme === 'light'}
            />
          )}

          <div className="task-dropzone hidden" />

          {tasksArchived.length > 0 && (
            <>
              <ShowArchivedButton
                count={tasksArchived.length}
                expanded={state.expandedCompleted[group.id]}
                onClick={() => store.toggleExpandedCompleted(group.id)}
                className={cn(!!tasksOpenSorted.length && 'mt-8')}
              />

              {state.expandedCompleted[group.id] && (
                <div className="proj-task-container mt-8 space-y-8">
                  {store.getClosedSortedTasks(group).map((task, i) => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      containerId={group.id}
                      style={{zIndex: 1000 - i}}
                      className={cn(
                        'proj-task-item',
                        Items.isHidden(Items.get(task.id)) && 'api-hide'
                      )}
                      onClick={e => store.selectMultipleTasksId(task.id, e)}
                    />
                  ))}
                  {tasksArchivedLeft > 0 && (
                    <ShowMoreButton
                      count={tasksArchivedLeft}
                      onClick={() => store.taskCountShowMore(group)}
                      noBorder={globals.theme === 'light'}
                    />
                  )}
                </div>
              )}
            </>
          )}
        </>
      </QueuedRender>
    </div>
  )
})

interface TaskProps {
  task: Hub
  idx: number
  hideLoader?: () => void
  showAtOnce?: boolean
}

export const Task = observer(({task, idx, showAtOnce}: TaskProps) => {
  const [, store] = useColumnStore()
  const [boardState] = useBoardStore()
  const group = store.column

  return (
    <QueuedRender
      queue={boardState.tasksRenderQueue}
      id={group?.location}
      showAtOnce={showAtOnce}
      placeholderClassName={taskCardPlaceholderClassName}
    >
      <TaskCardDrag task={task} index={idx} />
    </QueuedRender>
  )
})

// When "Show archived" button is clicked, archived task list is displayed at
// the bottom of the column container and is not visible, which baffles users.
// This hook scrolls to the first archived task so that some archived tasks
// become visible.
const useScrollToFirstArchived = () => {
  const [state, store] = useColumnStore()
  const group = store.column
  const showArchived = state.expandedCompleted[group.id]

  useEffect(() => {
    if (showArchived) {
      const taskId = store.getClosedSortedTasks(group)[0]?.id
      if (taskId) {
        const scrollableEl = store.state.scrollableEl
        const taskEl = scrollableEl?.querySelector(`[data-task-id="${taskId}"]`)
        if (scrollableEl && taskEl) {
          const dy = getScrollYToCenterDelta(scrollableEl, taskEl)
          scrollableEl.scrollTop += dy
        }
      }
    }
  }, [store, showArchived])
}
